/**
 * Utility functions for Vector Tile operations
 */

import {isMapPolygonFillVisible} from '@components/openstreetmap/constants/map-polygon-fill-visibility.state';

declare var L: any;

/**
 * Convert lat/lng to tile coordinates (z/x/y)
 */
export function latLngToTileCoords(lat: number, lng: number, zoom: number): { z: number, x: number, y: number } {
  const x = Math.floor((lng + 180) / 360 * Math.pow(2, zoom));
  const y = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
  return { z: zoom, x, y };
}

/**
 * Convert lat/lng to pixel position within a tile
 */
export function latLngToTilePixel(lat: number, lng: number, z: number, tileX: number, tileY: number, extent: number = 4096): { x: number, y: number } {
  // Calculate the position in world coordinates (0-1 range)
  const worldX = (lng + 180) / 360;
  const worldY = (1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2;

  // Scale to current zoom level
  const scale = Math.pow(2, z);
  const pixelX = worldX * scale;
  const pixelY = worldY * scale;

  // Get position within tile (0-extent range)
  const x = (pixelX - tileX) * extent;
  const y = (pixelY - tileY) * extent;

  return { x, y };
}

/**
 * Check if a point is inside a polygon (ray casting algorithm)
 */
export function isPointInPolygon(x: number, y: number, geometry: any[]): boolean {
  for (const ring of geometry) {
    if (isPointInRing(x, y, ring)) {
      return true;
    }
  }
  return false;
}

/**
 * Check if point is inside a ring using ray casting
 */
export function isPointInRing(x: number, y: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];

    const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Check if a point is near a line
 */
export function isPointNearLine(x: number, y: number, geometry: any[], tolerance: number): boolean {
  for (const line of geometry) {
    for (let i = 0; i < line.length - 1; i++) {
      const dist = pointToSegmentDistance(x, y, line[i][0], line[i][1], line[i + 1][0], line[i + 1][1]);
      if (dist < tolerance) return true;
    }
  }
  return false;
}

/**
 * Calculate distance from point to line segment
 */
export function pointToSegmentDistance(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
  }

  let t = ((px - x1) * dx + (py - y1) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));

  const nearestX = x1 + t * dx;
  const nearestY = y1 + t * dy;

  return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
}

/**
 * Check if a point is near another point
 */
export function isPointNearPoint(x: number, y: number, geometry: any[], tolerance: number): boolean {
  for (const point of geometry) {
    const dist = Math.sqrt((x - point[0]) ** 2 + (y - point[1]) ** 2);
    if (dist < tolerance) return true;
  }
  return false;
}

/**
 * Find feature at a specific point in tile coordinates
 */
export function findFeatureAtPoint(
  tileFeatures: any[],
  x: number,
  y: number,
  featuresById: Map<number, any>,
  tolerance: number = 50
): { tileFeature: any, originalFeature: any } | null {
  // Check features in reverse order (top features first)
  for (let i = tileFeatures.length - 1; i >= 0; i--) {
    const feature = tileFeatures[i];
    const geometry = feature.geometry;
    const type = feature.type;

    let found = false;

    if (type === 3) {
      // Polygon - check if point is inside
      found = isPointInPolygon(x, y, geometry);
    } else if (type === 2) {
      // LineString - check if point is near line
      found = isPointNearLine(x, y, geometry, tolerance);
    } else if (type === 1) {
      // Point - check if point is near
      found = isPointNearPoint(x, y, geometry, tolerance);
    }

    if (found) {
      const originalFeature = getOriginalFeature(feature, featuresById);
      return { tileFeature: feature, originalFeature };
    }
  }

  return null;
}

/**
 * Get the original GeoJSON feature with properties
 */
export function getOriginalFeature(tileFeature: any, featuresById: Map<number, any>): any {
  // Try to get by ID first
  if (tileFeature.id !== undefined && featuresById.has(tileFeature.id)) {
    return featuresById.get(tileFeature.id);
  }

  // If tags exist, return feature with tags as properties
  if (tileFeature.tags) {
    return { properties: tileFeature.tags };
  }

  // Fallback - return empty properties
  return { properties: {} };
}

/**
 * Calculate bounds from a FeatureCollection
 */
export function calculateBounds(featureCollection: any): any {
  let minLat = Infinity, maxLat = -Infinity;
  let minLng = Infinity, maxLng = -Infinity;

  const processCoords = (coords: any) => {
    if (typeof coords[0] === 'number') {
      // [lng, lat]
      minLng = Math.min(minLng, coords[0]);
      maxLng = Math.max(maxLng, coords[0]);
      minLat = Math.min(minLat, coords[1]);
      maxLat = Math.max(maxLat, coords[1]);
    } else {
      // Nested array
      for (const c of coords) {
        processCoords(c);
      }
    }
  };

  for (const feature of featureCollection.features) {
    if (feature.geometry?.coordinates) {
      processCoords(feature.geometry.coordinates);
    }
  }

  if (minLat === Infinity) {
    return null;
  }

  return L.latLngBounds(
    L.latLng(minLat, minLng),
    L.latLng(maxLat, maxLng)
  );
}

/**
 * Render a single feature on canvas
 */
export function renderFeature(
  ctx: CanvasRenderingContext2D,
  feature: any,
  scale: number,
  style?: {
    strokeStyle?: string;
    fillStyle?: string;
    lineWidth?: number;
  }
): void {
  const geometry = feature.geometry;
  const type = feature.type;

  if (style) {
    if (style.strokeStyle) ctx.strokeStyle = style.strokeStyle;
    if (style.fillStyle) ctx.fillStyle = style.fillStyle;
    if (style.lineWidth) ctx.lineWidth = style.lineWidth;
  }

  if (type === 1) {
    // Point
    for (let i = 0; i < geometry.length; i++) {
      const p = geometry[i];
      const x = p[0] * scale;
      const y = p[1] * scale;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (type === 2) {
    // LineString
    for (let i = 0; i < geometry.length; i++) {
      const ring = geometry[i];
      ctx.beginPath();
      for (let j = 0; j < ring.length; j++) {
        const p = ring[j];
        const x = p[0] * scale;
        const y = p[1] * scale;
        if (j === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
  } else if (type === 3) {
    // Polygon
    ctx.beginPath();
    for (let i = 0; i < geometry.length; i++) {
      const ring = geometry[i];
      for (let j = 0; j < ring.length; j++) {
        const p = ring[j];
        const x = p[0] * scale;
        const y = p[1] * scale;
        if (j === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
    }
    if (isMapPolygonFillVisible()) {
      ctx.fill();
    }
    ctx.stroke();
  }
}

/**
 * Create popup HTML content from properties
 */
export function createPopupContent(properties: any): string {
  if (!properties || Object.keys(properties).length === 0) {
    return '<em>No properties</em>';
  }

  let html = '<div class="geo-popup">';
  for (const [key, value] of Object.entries(properties)) {
    if (value !== null && value !== undefined && key !== 'id') {
      html += `<div><strong>${key}:</strong> ${value}</div>`;
    }
  }
  html += '</div>';
  return html;
}

/**
 * Interface for Vector Tile Layer data
 */
export interface VectorTileLayerData {
  tileIndex: any;
  featuresById: Map<number, any>;
  featureCollection: any;
  selectedFeatureId: number | null;
}
